import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { MODAL_TRYIT } from '@/components/organisms/Modals/ModalConductor';
import { useBrainId } from '@/hooks/useBrainId';
import { pushModal, updateModal } from '@/redux/modals/actions';
import { selectIsTryItModalOpen } from '@/redux/modals/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { urlWithoutSessionId } from '@/util/sessionIdExtractor';

/** Builds row hrefs that deep-link the replay modal via ?session_id=, preserving current query params. */
export const useSessionReplayHref = () => {
  const { search } = useLocation();

  // Serialize the invariant query params once; each row href is then a cheap concat
  const base = useMemo(() => {
    const params = new URLSearchParams(search);
    params.delete('session_id');
    const str = params.toString();
    return str ? `${str}&` : '';
  }, [search]);

  return useCallback(
    (sessionId: string) => `?${base}session_id=${encodeURIComponent(sessionId)}`,
    [base]
  );
};

export const useModalTryIt = (
  sessionId?: string | null,
  options?: { showNavigationHint?: boolean }
) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const brainId = useBrainId();
  const slug = useSelector(selectAccountSlug);
  const openedSessionRef = useRef<string | null>(null);

  const isTryItModalOpen = useSelector(selectIsTryItModalOpen);
  // Use a ref so callbacks always read the latest value without being recreated on open/close
  const isTryItModalOpenRef = useRef(isTryItModalOpen);
  isTryItModalOpenRef.current = isTryItModalOpen;

  // Reset the opened session ref when the TryIt modal is closed
  // This is more reliable than checking sessionId becoming null
  useEffect(() => {
    if (!isTryItModalOpen) {
      openedSessionRef.current = null;
    }
  }, [isTryItModalOpen]);

  const clearSessionIdFromUrl = () => urlWithoutSessionId();

  const handleReplayClick = useCallback(
    (clickedSession?: string, sessionStart?: string) => {
      if (brainId) {
        // Determine which session ID to use
        const sessionToUse = clickedSession || sessionId;

        // Open modal only if it's a different session than the one already opened
        const shouldOpenModal = sessionToUse && openedSessionRef.current !== sessionToUse;

        if (shouldOpenModal) {
          openedSessionRef.current = sessionToUse;
          if (isTryItModalOpenRef.current) {
            dispatch(updateModal({ brainId, sessionId: sessionToUse, sessionStart }));
          } else {
            dispatch(
              pushModal(MODAL_TRYIT, {
                brainId,
                sessionId: sessionToUse,
                sessionStart,
                showNavigationHint: options?.showNavigationHint,
              })
            );
          }
        }

        const path = `/${slug}/ai-agents/${brainId}/logs?session_id=${sessionToUse}`;

        navigate(path, { replace: true });
      }
    },
    [brainId, dispatch, navigate, options?.showNavigationHint, sessionId, slug]
  );

  const handleAccountReplayClick = useCallback(
    (clickedSession?: string, sessionBrainId?: string, sessionStart?: string) => {
      // Determine which session ID to use
      const sessionToUse = clickedSession || sessionId;

      // Open modal only if it's a different session than the one already opened
      const shouldOpenModal = sessionToUse && openedSessionRef.current !== sessionToUse;

      if (shouldOpenModal) {
        openedSessionRef.current = sessionToUse;
        if (isTryItModalOpenRef.current) {
          dispatch(updateModal({ sessionId: sessionToUse, brainId: sessionBrainId, sessionStart }));
        } else {
          dispatch(
            pushModal(MODAL_TRYIT, {
              sessionId: sessionToUse,
              brainId: sessionBrainId,
              sessionStart,
              showNavigationHint: options?.showNavigationHint,
            })
          );
        }
      }

      // Always reflect the selected session in the URL for deep-linking
      if (sessionToUse) {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('session_id', sessionToUse);
        navigate(
          { pathname: window.location.pathname, search: currentUrl.search },
          { replace: true }
        );
      }
    },
    [dispatch, navigate, options?.showNavigationHint, sessionId]
  );

  return { handleReplayClick, handleAccountReplayClick, clearSessionIdFromUrl };
};
